/**
 * Vendor Display Customization
 * Filters vendors based on an allow list and applies aliases.
 * Handles dynamic content updates via MutationObserver.
 */
(function() {
  let allowList = null;
  let aliasList = null;
  let isUpdating = false;

  /**
   * Cleans and parses JSON that might contain single-line or multi-line comments.
   * Standard JSON.parse() fails on comments, which are present in the asset files.
   */
  function parseJSONWithComments(text) {
    try {
      // This regex removes comments while ignoring them within strings
      const cleanText = text.replace(/\\"|"(?:\\"|[^"])*"|(\/\/.*|\/\*[\s\S]*?\*\/)/g, (m, g) => g ? "" : m);
      return JSON.parse(cleanText);
    } catch (e) {
      console.error('Vendor Display: Failed to parse JSON configuration', e);
      return null;
    }
  }

  /**
   * Loads configuration from assets.
   */
  async function loadVendorConfig() {
    try {
      const allowUrl = window.theme?.assets?.vendorAllowList || `${window.location.origin}/assets/custom-vendor-display-list.json`;
      const aliasUrl = window.theme?.assets?.vendorAliasList || `${window.location.origin}/assets/custom-vendor-display-alias-list.json`;

      //console.log('Vendor Display: Fetching config from:', { allowUrl, aliasUrl });
      const [allowRes, aliasRes] = await Promise.all([
        fetch(allowUrl),
        fetch(aliasUrl)
      ]);

      if (!allowRes.ok || !aliasRes.ok) {
        throw new Error(`Fetch failed: AllowList ${allowRes.status}, AliasList ${aliasRes.status}`);
      }

      const allowText = await allowRes.text();
      const aliasText = await aliasRes.text();

      allowList = parseJSONWithComments(allowText);
      const aliasData = parseJSONWithComments(aliasText);
      
      // Alias data is expected to be an array with one object: [{ "Old": "New" }]
      aliasList = (Array.isArray(aliasData) ? aliasData[0] : aliasData) || {};

      if (allowList) {
        console.log('Vendor display config loaded successfully');
        updateVendorNames();
      }
    } catch (error) {
      console.error('Error loading vendor display config:', error);
      allowList = [];
      aliasList = {};
    }
  }

  /**
   * Updates vendor names in the DOM based on the allowList and aliasList.
   */
  function updateVendorNames() {
    // Wait until config is loaded or if already updating
    if (!allowList || !aliasList || isUpdating) return;

    isUpdating = true;
    try {
      const selectors = '.card-vendor, .spf-product-card__vendor a, .gf-search-item-product-vendor, #newproduct-headding .info h2, #newproduct-headding h2, .rec-item h2';
      const elements = document.querySelectorAll(selectors);

      elements.forEach(el => {
        // Store original name in a data attribute to preserve it across multiple runs
        if (!el.dataset.originalVendor) {
          el.dataset.originalVendor = el.textContent.trim();
        }

        const originalName = el.dataset.originalVendor;
        if (!originalName) return;

        // 1. Check if the vendor is in the allow list (case-insensitive)
        const isAllowed = allowList.some(item => item && item.trim().toLowerCase() === originalName.toLowerCase());

        if (!isAllowed) {
          // Hide ONLY the vendor element, keeping the product visible
          if (el.style.display !== 'none') {
            el.style.display = 'none';
          }
          return;
        }

        // 2. If allowed, ensure it is visible
        if (el.style.display === 'none') {
          el.style.display = '';
        }

        // 3. Apply alias if one exists for the original name (case-insensitive)
        const aliasKey = Object.keys(aliasList).find(key => key.trim().toLowerCase() === originalName.toLowerCase());
        const targetText = aliasKey ? aliasList[aliasKey] : originalName;
        
        if (el.textContent !== targetText) {
          el.textContent = targetText;
        }
      });
    } finally {
      // Small delay to ensure any mutations triggered by this function are ignored
      setTimeout(() => {
        isUpdating = false;
      }, 0);
    }
  }

  // MutationObserver to handle dynamically loaded products (AJAX, Search, etc.)
  const productObserver = new MutationObserver((mutations) => {
    if (isUpdating) return;

    let shouldUpdate = false;
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        shouldUpdate = true;
        break;
      }
    }
    if (shouldUpdate) {
      updateVendorNames();
    }
  });

  /**
   * Sets up observers for product grid containers.
   */
  function initObservers() {
    // Observe known product grid containers and search popups
    const gridIds = ['gf-products', 'product-grid-ajax', 'glFilter-search-popup', 'CollectionProductGrid', 'MainContent', 'newproduct-headding', 'newproduct-recomendeds'];
    gridIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        productObserver.observe(el, { childList: true, subtree: true });
      }
    });

    // If search popup isn't in DOM yet, watch body for its addition
    if (!document.getElementById('glFilter-search-popup')) {
      const bodyObserver = new MutationObserver((mutations, obs) => {
        const popup = document.getElementById('glFilter-search-popup');
        if (popup) {
          productObserver.observe(popup, { childList: true, subtree: true });
          obs.disconnect(); // Stop watching body once found
          updateVendorNames();
        }
      });
      bodyObserver.observe(document.body, { childList: true });
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      loadVendorConfig();
      initObservers();
    });
  } else {
    loadVendorConfig();
    initObservers();
  }

  // Re-run on Shopify section load event for editor compatibility
  document.addEventListener('shopify:section:load', () => {
    updateVendorNames();
  });

})();
